import { useCallback, useEffect, useMemo, useState } from "react";
import emitter from "../../../Utils/events/eventBus";
import { MAX_WEEKLY_TASKS, MOCK_WEEKS, TASK_GROUP_ORDER, buildDemoAnswers } from "../mockData";
import { buildDemoAnnouncements } from "../announcementMock";

// Mock data layer for "Grow your community". The page, the sidebar Get Started
// popup and the dashboard card all read through these hooks, so a task ticked
// in one place is ticked everywhere. State lives in localStorage and changes
// are broadcast on the app event bus. When the backend lands, only these hooks
// change — the components keep the same shape.

const STORAGE_KEY = "growCommunity.mock.v1";
const WEEK_KEY = "growCommunity.mock.week";
const CHANGED = "grow-community-changed";

const readState = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
};

const writeState = (next) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage unavailable (private mode) — the page still works for this visit.
  }
  emitter.emit(CHANGED);
};

/** Re-render every grow hook (page, sidebar popup, dashboard card). */
export const notifyGrowChanged = () => emitter.emit(CHANGED);

// `?week=1|2|all` picks the mock set; remembered for the session so the
// sidebar popup on other pages shows the same one.
export const getMockWeek = () => {
  const fromUrl = new URLSearchParams(window.location.search).get("week");
  if (fromUrl && MOCK_WEEKS[fromUrl]) {
    sessionStorage.setItem(WEEK_KEY, fromUrl);
    return fromUrl;
  }
  const saved = sessionStorage.getItem(WEEK_KEY);
  return saved && MOCK_WEEKS[saved] ? saved : "1";
};

const useGrowState = () => {
  const [state, setState] = useState(readState);

  useEffect(() => {
    const sync = () => setState(readState());
    emitter.on(CHANGED, sync);
    return () => emitter.off(CHANGED, sync);
  }, []);

  const update = useCallback((fn) => writeState(fn(readState())), []);
  return [state, update];
};

const markInWeek = (week, bucket, id) => (state) => ({
  ...state,
  [week]: {
    ...state[week],
    [bucket]: { ...(state[week]?.[bucket] || {}), [id]: true },
  },
});

/** Vars may be flat ({ count }) or per language ({ he: {...}, en: {...} }). */
export const resolveVars = (vars, lang) =>
  vars && (vars.he || vars.en) ? vars[lang] ?? vars.en : vars || {};

export const useWeeklyTasks = () => {
  const week = getMockWeek();
  const [state, update] = useGrowState();

  const tasks = useMemo(() => {
    const done = state[week]?.tasksDone || {};
    const { tasks: weekTasks, uncapped } = MOCK_WEEKS[week];
    const sorted = [...weekTasks].sort(
      (a, b) => TASK_GROUP_ORDER.indexOf(a.group) - TASK_GROUP_ORDER.indexOf(b.group),
    );
    return (uncapped ? sorted : sorted.slice(0, MAX_WEEKLY_TASKS)).map((task) => ({
      ...task,
      done: !!done[task.id] || !!task.initiallyDone,
    }));
  }, [state, week]);

  const completed = tasks.filter((task) => task.done).length;
  const percentage = tasks.length ? Math.round((completed / tasks.length) * 100) : 0;

  const markDone = useCallback((id) => update(markInWeek(week, "tasksDone", id)), [update, week]);

  return { tasks, completed, total: tasks.length, percentage, markDone };
};

export const useShareItems = () => {
  const week = getMockWeek();
  const [state, update] = useGrowState();

  const items = useMemo(() => {
    const shared = state[week]?.shared || {};
    return MOCK_WEEKS[week].items.map((item) => ({
      ...item,
      shared: !!shared[item.id] || !!item.initiallyShared,
    }));
  }, [state, week]);

  const markShared = useCallback((id) => update(markInWeek(week, "shared", id)), [update, week]);

  return { items, markShared };
};

/**
 * A saved list the manager builds up (AI answers, community messages). In "all
 * situations" demo entries are appended; deleting a demo only hides it, and
 * edits to a demo are kept as a patch.
 */
const useSavedList = ({ listKey, hiddenKey, patchesKey, buildDemos, uiLang }) => {
  const week = getMockWeek();
  const [state, update] = useGrowState();
  const withDemos = !!MOCK_WEEKS[week].demoAnswers;
  const demos = useMemo(() => (withDemos ? buildDemos(uiLang) : []), [withDemos, buildDemos, uiLang]);

  const entries = useMemo(() => {
    const own = state[listKey] || [];
    const hidden = state[hiddenKey] || {};
    const patches = state[patchesKey] || {};
    const visibleDemos = demos
      .filter((entry) => !hidden[entry.id])
      .map((entry) => ({ ...entry, ...(patches[entry.id] || {}) }));
    return [...own, ...visibleDemos];
  }, [state, demos, listKey, hiddenKey, patchesKey]);

  const add = useCallback(
    (entry) => update((s) => ({ ...s, [listKey]: [entry, ...(s[listKey] || [])] })),
    [update, listKey],
  );
  const patch = useCallback(
    (id, changes) =>
      update((s) =>
        id.startsWith("demo-")
          ? { ...s, [patchesKey]: { ...(s[patchesKey] || {}), [id]: { ...(s[patchesKey]?.[id] || {}), ...changes } } }
          : { ...s, [listKey]: (s[listKey] || []).map((e) => (e.id === id ? { ...e, ...changes } : e)) },
      ),
    [update, listKey, patchesKey],
  );
  const remove = useCallback(
    (id) =>
      update((s) =>
        id.startsWith("demo-")
          ? { ...s, [hiddenKey]: { ...(s[hiddenKey] || {}), [id]: true } }
          : { ...s, [listKey]: (s[listKey] || []).filter((e) => e.id !== id) },
      ),
    [update, listKey, hiddenKey],
  );

  return { entries, add, patch, remove };
};

/** Saved answers from the AI reply tool — the `?preview=1` mock (the real list is in useCommunityAi). */
export const useMockSavedAnswers = (uiLang) => {
  const { entries, add, patch, remove } = useSavedList({
    listKey: "answers",
    hiddenKey: "demoHidden",
    patchesKey: "demoPatches",
    buildDemos: buildDemoAnswers,
    uiLang,
  });
  return { answers: entries, addAnswer: add, updateAnswer: patch, removeAnswer: remove };
};

/** Saved messages from the community-message tool — the `?preview=1` mock (the real list is in useCommunityAi). */
export const useMockSavedAnnouncements = (uiLang) => {
  const { entries, add, remove } = useSavedList({
    listKey: "announcements",
    hiddenKey: "announcementDemoHidden",
    patchesKey: "announcementDemoPatches",
    buildDemos: buildDemoAnnouncements,
    uiLang,
  });
  return { announcements: entries, addAnnouncement: add, removeAnnouncement: remove };
};
